import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { X, UserRound } from "lucide-react";
import api from "@/lib/api";
import { money } from "@/lib/format";

const badge = "text-xs px-2.5 py-1 rounded-full font-medium bg-[#D98E73]/15 text-[#B5794E]";
const summary = (u) => `${u.bookings} booking${u.bookings === 1 ? "" : "s"}${u.no_shows ? ` · ${u.no_shows} no-show${u.no_shows === 1 ? "" : "s"}` : ""}`;

export function GuestRow({ u, onOpen }) {
  return (
    <tr data-testid={`admin-guest-row-${u.guest_contact_id}`} className="hover:bg-[#F9F7F3]">
      <td className="px-3 py-3" />
      <td className="px-4 py-3 text-[#1A242B]">{u.name || "—"}</td>
      <td className="px-4 py-3 text-[#4A5D6B]">{u.email}</td>
      <td className="px-4 py-3 text-[#4A5D6B] hidden md:table-cell">{u.phone || "—"}</td>
      <td className="px-4 py-3 hidden lg:table-cell">—</td>
      <td className="px-4 py-3"><span className={badge}>guest</span></td>
      <td className="px-4 py-3 text-[#8FA89B] text-xs hidden lg:table-cell">{String(u.created_at || "").slice(0, 10)}</td>
      <td className="px-4 py-3 text-xs text-[#4A5D6B]">{summary(u)}</td>
      <td className="px-4 py-3 text-right"><button onClick={() => onOpen(u)} data-testid={`admin-guest-view-${u.guest_contact_id}`} className="text-[#144F75] hover:underline text-xs font-medium">View</button></td>
    </tr>
  );
}

export function GuestCard({ u, onOpen }) {
  return (
    <div data-testid={`admin-guest-card-${u.guest_contact_id}`} className="rounded-xl border border-[#E5E0D8] bg-white p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0"><p className="font-medium text-[#1A242B] break-words">{u.name || "—"}</p><p className="text-sm text-[#4A5D6B] break-all">{u.email}</p></div>
        <span className={badge}>guest</span>
      </div>
      <p className="text-sm text-[#4A5D6B] mt-2">{u.phone || "No phone"} · {summary(u)}</p>
      <button onClick={() => onOpen(u)} data-testid={`admin-guest-view-m-${u.guest_contact_id}`} className="eyw-btn !py-2 text-xs w-full justify-center mt-3">View guest record</button>
    </div>
  );
}

export default function GuestContactModal({ guest, onClose }) {
  const { data } = useQuery({ queryKey: ["guest-contact", guest.guest_contact_id], queryFn: () => api.get(`/admin/guest-contacts/${guest.guest_contact_id}`).then((r) => r.data) });
  const c = data?.contact || guest;
  return createPortal(
    <div className="fixed inset-0 z-[210] bg-black/40 flex items-end sm:items-center justify-center sm:p-4" data-testid="guest-contact-modal">
      <div className="bg-[#FDFBF7] w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl p-5 sm:p-7">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="h-11 w-11 rounded-full bg-[#D98E73]/15 grid place-items-center text-[#B5794E]"><UserRound size={20} /></span>
            <div>
              <h3 className="font-serif text-2xl text-[#1A242B]">{c.name || c.email}</h3>
              <p className="text-sm text-[#4A5D6B]">{c.email} · {c.phone || "no phone"}</p>
            </div>
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="guest-contact-close" className="h-10 w-10 grid place-items-center rounded-full hover:bg-black/5"><X size={18} /></button>
        </div>
        <p className="text-xs text-[#8FA89B] mt-3">Guest contact — no account yet. If they sign up with this email, their bookings and agreements move into the new account automatically.</p>
        <div className="grid grid-cols-3 gap-2 mt-4">
          {[["Bookings", c.bookings ?? 0], ["No-shows", c.no_shows ?? 0], ["Agreements", data?.agreements?.length ?? 0]].map(([l, v]) => (
            <div key={l} className="rounded-xl bg-white border border-[#E5E0D8] p-3 text-center" data-testid={`guest-stat-${l.toLowerCase()}`}><p className="font-serif text-2xl text-[#1A242B]">{v}</p><p className="text-[11px] text-[#8FA89B]">{l}</p></div>
          ))}
        </div>
        <h4 className="eyw-label mt-6 mb-2">Booking history</h4>
        <ul className="divide-y divide-[#E5E0D8] rounded-2xl border border-[#E5E0D8] bg-white">
          {(data?.bookings || []).map((b) => (
            <li key={b.id} className="p-3 text-sm flex flex-wrap justify-between gap-2" data-testid={`guest-booking-${b.id}`}>
              <span className="text-[#1A242B]">{b.service_title} <span className="text-[#8FA89B]">· {b.date} {b.start_time}{b.location ? ` · ${b.location}` : ""}</span></span>
              <span className={`text-xs ${b.status === "no_show" ? "text-[#B45151] font-semibold" : "text-[#4A5D6B]"}`}>{b.status.replace("_", "-")} · {money(b.price || 0)} {b.payment_status}</span>
            </li>
          ))}
          {data && !data.bookings.length && <li className="p-3 text-sm text-[#8FA89B]">No bookings.</li>}
        </ul>
        {data?.agreements?.length > 0 && (
          <>
            <h4 className="eyw-label mt-6 mb-2">Signed agreements</h4>
            <ul className="text-sm space-y-1">{data.agreements.map((a) => <li key={a.id} className="text-[#4A5D6B]">{a.title} · v{a.version} · {String(a.signed_at).slice(0, 10)}</li>)}</ul>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
